import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Activity,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  ExternalLink,
  FileText,
  Loader2,
  RefreshCw,
  Scissors,
  Plus,
  Trash2,
} from 'lucide-react'

import { Button } from '@components/ui/button'
import { Input } from '@components/ui/input'
import { Textarea } from '@components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@components/ui/select'
import { useActivePatient } from '@context/ActivePatientContext'
import { useClinicalHistory } from '@/hooks/useClinicalHistory'
import { usePatientConsultations } from '@/hooks/usePatientConsultations'
import { usePatientFiles } from '@/hooks/useFiles'
import { ClinicalHistoryDocument } from '@/components/patient/ClinicalHistoryDocument'
import { ConsultationNoteDialog } from '@/components/consultation/ConsultationNoteDialog'
import { EditGinecoObstetricosDialog } from '@/components/patients/EditGinecoObstetricosDialog'
import { AddHistoryItemDialog } from '@/components/patients/AddHistoryItemDialog'
import { useUpdatePatient } from '@/hooks/usePatients'
import { useAuthStore } from '@/hooks/useAuthStore'
import { useToast } from '@/hooks/use-toast'
import { getApiErrorMessage } from '@services/api'
import { cn } from '@lib/utils'
import { AxiosError } from 'axios'
import type { AllergyEntry, FamilyHistoryEntry, PathologicalEntry, SocialHistory, PatientCreateRequest, ClinicalHistory } from '@/types'
import type { LabFile } from '@/types/file'
import type { ConsultationSummary } from '@/services/consultation.service'

const statusLabels: Record<string, string> = {
  active: 'Activo',
  resolved: 'Resuelto',
  chronic: 'Crónico',
  under_follow_up: 'En seguimiento',
}

const ITEM_TYPE_LABELS: Record<string, string> = {
  chronic_degenerative: 'Enf. crónico-degenerativa',
  surgery: 'Cirugía',
  trauma: 'Traumatismo',
  transfusion: 'Transfusión',
  allergy: 'Alergia',
  hospitalization: 'Hospitalización',
  vaccination: 'Vacunación',
  current_medication: 'Medicamento',
  other: 'Otro',
}

const socialStatusLabels: Record<string, string> = {
  never: 'Nunca',
  former: 'Anteriormente',
  current: 'Actualmente',
  none: 'Ninguno',
  occasional: 'Ocasional',
  regular: 'Regular',
}

const typeLabels: Record<string, string> = {
  drug: 'Medicamento',
  food: 'Alimento',
  environmental: 'Ambiental',
  other: 'Otro',
}

// Soft "warm clinical" palette — purposeful color that maps risk/category, never
// saturated: muted amber (active/chronic), sage green (resolved/surgical), stone
// gray (non-pathological), gray-blue (heredofamiliar/administrative).
const PAL = {
  active: { dot: 'bg-amber-500/80', text: 'text-amber-700', chip: 'border-amber-500/40 text-amber-800', bar: 'border-l-amber-400', soft: 'bg-amber-50/50', softHover: 'hover:bg-amber-50' },
  surgical: { dot: 'bg-emerald-600/70', text: 'text-emerald-700', chip: 'border-emerald-600/40 text-emerald-800', bar: 'border-l-emerald-400', soft: 'bg-emerald-50/50', softHover: 'hover:bg-emerald-50' },
  neutral: { dot: 'bg-stone-400', text: 'text-stone-500', chip: 'border-stone-400/50 text-stone-600', bar: 'border-l-stone-300', soft: 'bg-stone-50/60', softHover: 'hover:bg-stone-100/60' },
  info: { dot: 'bg-slate-400', text: 'text-slate-600', chip: 'border-slate-400/50 text-slate-600', bar: 'border-l-slate-400', soft: 'bg-slate-50/50', softHover: 'hover:bg-slate-50' },
} as const
type Cat = keyof typeof PAL

const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s)
const severityLabel = (s: string) =>
  s === 'severe' ? 'Severa' : s === 'moderate' ? 'Moderada' : 'Leve'

export function HistoriaClinicaTab() {
  const { activePatientData, refreshActivePatient } = useActivePatient()
  const { mutate: updatePatient, isPending } = useUpdatePatient()
  const { toast } = useToast()
  const navigate = useNavigate()
  const currentUser = useAuthStore((s) => s.user)
  const [ginecoEditOpen, setGinecoEditOpen] = useState(false)
  const [selectedEntry, setSelectedEntry] = useState<PathologicalEntry | null>(null)
  const [openNoteId, setOpenNoteId] = useState<string | null>(null)
  // Accordion: undefined → use the computed default; null → all collapsed; key → that panel.
  const [userOpenKey, setUserOpenKey] = useState<string | null | undefined>(undefined)
  const [addHistoryItemOpen, setAddHistoryItemOpen] = useState(false)

  // Focused edit dialogs state
  const [editFamiliaresOpen, setEditFamiliaresOpen] = useState(false)
  const [editSocialOpen, setEditSocialOpen] = useState(false)
  const [editTratamientosOpen, setEditTratamientosOpen] = useState(false)
  const [editAlergiasOpen, setEditAlergiasOpen] = useState(false)
  const [editCirugiasOpen, setEditCirugiasOpen] = useState(false)
  const [editObservacionesOpen, setEditObservacionesOpen] = useState(false)

  // Temporary form data for each focused dialog
  const [familiaresForm, setFamiliaresForm] = useState<FamilyHistoryEntry[]>([])
  const [socialForm, setSocialForm] = useState<SocialHistory>({ smoking_status: 'never', alcohol_status: 'never', drug_use_status: 'never', exercise_status: 'none' })
  const [tratamientosForm, setTratamientosForm] = useState<string[]>([])
  const [alergiasForm, setAlergiasForm] = useState<AllergyEntry[]>([])
  const [cirugiasForm, setCirugiasForm] = useState<PathologicalEntry[]>([])
  const [observacionesForm, setObservacionesForm] = useState('')


  if (!activePatientData) return null

  const patient = activePatientData

  // Clinical history existence / creation
  const { data: clinicalHistory } = useClinicalHistory(patient.id, { enabled: !!patient.id })
  const { signed: signedNotes } = usePatientConsultations(patient.id)
  const { data: patientFilesData } = usePatientFiles(patient.id)
  const patientFiles = patientFilesData?.files ?? []

  const isFemale = patient.gender === 'female'

  const saveField = <K extends keyof PatientCreateRequest>(
    field: K,
    value: PatientCreateRequest[K],
    successMsg: string
  ) => {
    updatePatient(
      { id: patient.id, data: { [field]: value } as Partial<PatientCreateRequest> },
      {
        onSuccess: () => {
          refreshActivePatient()
          toast({ title: 'Actualizado', description: successMsg })
        },
        onError: (err) => {
          const message = err instanceof AxiosError
            ? getApiErrorMessage(err, 'No se pudo guardar.')
            : 'No se pudo guardar.'
          toast({
            title: 'Error',
            description: message,
            variant: 'destructive',
          })
        },
      }
    )
  }

  // Open handlers that pre-populate form data
  const openFamiliares = () => {
    setFamiliaresForm([...(patient.family_history ?? [])])
    setEditFamiliaresOpen(true)
  }
  const openSocial = () => {
    setSocialForm(patient.social_history ?? { smoking_status: 'never', alcohol_status: 'never', drug_use_status: 'never', exercise_status: 'none' })
    setEditSocialOpen(true)
  }
  const openAlergias = () => {
    setAlergiasForm([...(patient.allergies ?? [])])
    setEditAlergiasOpen(true)
  }
  const openTratamientos = () => {
    setTratamientosForm([...(patient.current_medications ?? [])])
    setEditTratamientosOpen(true)
  }
  const openCirugias = () => {
    setCirugiasForm([...(patient.pathological_history ?? [])])
    setEditCirugiasOpen(true)
  }
  const openObservaciones = () => {
    setObservacionesForm(patient.clinical_observations ?? '')
    setEditObservacionesOpen(true)
  }

  const handleSaveHistoryItem = (entry: PathologicalEntry) => {
    const existing = patient.pathological_history ?? []
    saveField('pathological_history', [...existing, entry], 'Antecedente registrado.')
    setAddHistoryItemOpen(false)
  }

  // ── Derived collections ───────────────────────────────────────────────────
  const familiares = patient.family_history ?? []
  const patologicos = patient.pathological_history ?? []
  const allergies = patient.allergies ?? []
  const medications = (patient.current_medications ?? []).filter(Boolean)
  const social = patient.social_history
  const gineco = patient.gynecological_history

  // Category of a pathological entry, for timeline/colour mapping.
  const patCat = (status: string): Cat =>
    status === 'resolved' ? 'surgical' : status === 'active' || status === 'chronic' || status === 'under_follow_up' ? 'active' : 'neutral'

  // Timeline milestones — dated pathological events, oldest → newest.
  const milestones = patologicos
    .filter((p) => p.condition?.trim())
    .map((p) => {
      // "Added later" = item was captured after the clinical history was already signed
      const addedLater =
        !!clinicalHistory?.attested_at &&
        clinicalHistory.status === 'signed' &&
        !!p.captured_at &&
        p.captured_at > clinicalHistory.attested_at

      // Year display: use date_year for year-only precision, otherwise slice diagnosis_date
      const year = p.date_precision === 'year_only' && p.date_year
        ? String(p.date_year)
        : p.diagnosis_date
          ? p.diagnosis_date.slice(0, 4)
          : p.date_approximate
            ? p.date_approximate
            : 's/f'

      return {
        year,
        sort: p.diagnosis_date ?? '9999',
        label: p.condition,
        cat: patCat(p.status),
        statusLabel: statusLabels[p.status] ?? p.status,
        entry: p,
        addedLater,
      }
    })
    .sort((a, b) => a.sort.localeCompare(b.sort))

  // Key points — the few items most critical for immediate care.
  const keyPoints: { label: string; cat: Cat }[] = []
  patologicos.forEach((p) => {
    if (p.condition?.trim() && (p.status === 'active' || p.status === 'chronic')) keyPoints.push({ label: p.condition, cat: 'active' })
  })
  allergies.forEach((a) => {
    if (a.name?.trim() && (a.severity === 'severe' || a.severity === 'moderate')) keyPoints.push({ label: `Alergia: ${a.name}`, cat: 'active' })
  })
  if (social?.smoking_status === 'current') keyPoints.push({ label: 'Fumador activo', cat: 'active' })
  const keyPointsShown = keyPoints.slice(0, 4)

  // Accordion panels (counts drive the badge + default-open choice).
  const dataPanels: { key: string; title: string; count: number; cat: Cat }[] = [
    { key: 'patologicos', title: 'Antecedentes personales patológicos', count: patologicos.length + allergies.length + medications.length, cat: 'active' },
    { key: 'familiares', title: 'Antecedentes heredofamiliares', count: familiares.length, cat: 'info' },
    { key: 'no_pat', title: 'Antecedentes personales no patológicos', count: social ? 4 : 0, cat: 'neutral' },
    ...(isFemale ? [{ key: 'gineco', title: 'Antecedentes gineco-obstétricos', count: gineco ? 1 : 0, cat: 'surgical' as Cat }] : []),
  ]
  const defaultOpenKey = dataPanels.find((p) => p.count > 0)?.key ?? dataPanels[0].key
  const openKey = userOpenKey === undefined ? defaultOpenKey : userOpenKey
  const toggle = (k: string) => setUserOpenKey(openKey === k ? null : k)

  const renderBody = (key: string) => {
    switch (key) {
      case 'patologicos':
        return (
          <div className="space-y-6">
            <SubBlock
              title="Antecedentes personales patológicos"
              action={
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setAddHistoryItemOpen(true)}
                    className="flex items-center gap-1 rounded bg-ml-primary/5 border border-ml-primary/20 px-2.5 py-1 text-[11px] font-medium text-ml-primary hover:bg-ml-primary/10 transition-colors"
                    title="Agregar antecedente individual"
                  >
                    <Plus className="h-3 w-3" />
                    Agregar
                  </button>
                  <EditLink onClick={openCirugias}>Editar todos</EditLink>
                </div>
              }
            >
              {patologicos.length > 0 ? (
                <HistoryTable
                  head={['Condición', 'Tipo', 'Estado', 'Fecha']}
                  rows={patologicos.map((e) => [
                    e.condition,
                    e.item_type ? (ITEM_TYPE_LABELS[e.item_type] ?? e.item_type) : '—',
                    statusLabels[e.status] ?? e.status,
                    e.date_precision === 'approximate' ? (e.date_approximate || '—')
                      : e.date_precision === 'year_only' ? (e.date_year ? String(e.date_year) : '—')
                      : (e.diagnosis_date || '—'),
                  ])}
                />
              ) : (
                <Placeholder text="Sin antecedentes patológicos registrados" onAdd={() => setAddHistoryItemOpen(true)} />
              )}
            </SubBlock>
            <SubBlock title="Alergias" action={<EditLink onClick={openAlergias} />}>
              {allergies.length > 0 ? (
                <HistoryTable
                  head={['Alergia', 'Tipo', 'Severidad', 'Reacción']}
                  rows={allergies.map((a) => [a.name, typeLabels[a.type] ?? a.type, severityLabel(a.severity), a.reaction || '—'])}
                />
              ) : (
                <Placeholder text="Sin alergias registradas" onAdd={openAlergias} />
              )}
            </SubBlock>
            <SubBlock title="Tratamientos actuales" action={<EditLink onClick={openTratamientos} />}>
              {medications.length > 0 ? (
                <ul className="space-y-1.5">
                  {medications.map((m, i) => (
                    <li key={`${m}-${i}`} className="flex gap-2 text-sm text-ml-on-surface">
                      <span className="text-ml-on-surface-variant/40">·</span>
                      <span>{m}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <Placeholder text="Sin tratamientos registrados" onAdd={openTratamientos} />
              )}
            </SubBlock>
          </div>
        )
      case 'familiares':
        return (
          <>
            <div className="mb-2 flex justify-end"><EditLink onClick={openFamiliares} /></div>
            {familiares.length > 0 ? (
              <HistoryTable
                head={['Parentesco', 'Condición', 'Notas']}
                rows={familiares.map((e) => [cap(e.relation) || '—', e.condition, e.notes || '—'])}
              />
            ) : (
              <Placeholder text="Sin antecedentes heredofamiliares registrados" onAdd={openFamiliares} />
            )}
          </>
        )
      case 'no_pat':
        return (
          <>
            <div className="mb-2 flex justify-end"><EditLink onClick={openSocial} /></div>
            {social ? (
              <div className="grid grid-cols-2 gap-x-8 gap-y-3 sm:grid-cols-4">
                <Stat label="Tabaquismo" value={socialStatusLabels[social.smoking_status]} />
                <Stat label="Alcohol" value={socialStatusLabels[social.alcohol_status]} />
                <Stat label="Sustancias" value={socialStatusLabels[social.drug_use_status]} />
                <Stat label="Actividad física" value={socialStatusLabels[social.exercise_status]} />
              </div>
            ) : (
              <Placeholder text="Sin antecedentes no patológicos registrados" onAdd={openSocial} />
            )}
          </>
        )
      case 'gineco':
        return (
          <>
            <div className="mb-2 flex justify-end"><EditLink onClick={() => setGinecoEditOpen(true)} /></div>
            {gineco ? (
              <div className="grid grid-cols-2 gap-x-8 gap-y-3 sm:grid-cols-4">
                <Stat label="Gestas" value={String(gineco.gestas)} />
                <Stat label="Partos" value={String(gineco.partos)} />
                <Stat label="Cesáreas" value={String(gineco.cesareas)} />
                <Stat label="Abortos" value={String(gineco.abortos)} />
                <Stat label="Menarquia" value={gineco.menarquia_age != null ? `${gineco.menarquia_age} años` : null} />
                <Stat label="FUM" value={gineco.fum || null} />
                <Stat label="Ciclo menstrual" value={gineco.ciclo_menstrual ? cap(gineco.ciclo_menstrual) : null} />
                <Stat label="Anticonceptivos" value={gineco.anticonceptivos || null} />
              </div>
            ) : (
              <Placeholder text="Sin antecedentes gineco-obstétricos registrados" onAdd={() => setGinecoEditOpen(true)} />
            )}
          </>
        )
      default:
        return null
    }
  }

  return (
    <div className="animate-fade-in space-y-6">
      {/* ── Key points strip — outlined, understated chips for immediate care ── */}
      {keyPointsShown.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-ml-outline-variant/20 bg-ml-surface-container-lowest/50 px-4 py-3">
          <span className="mr-1 text-[10px] font-bold uppercase tracking-[0.15em] text-ml-on-surface-variant/60">
            Puntos clave
          </span>
          {keyPointsShown.map((it, i) => (
            <span key={i} className={cn('rounded border bg-transparent px-3 py-1 text-xs font-medium', PAL[it.cat].chip)}>
              {it.label}
            </span>
          ))}
        </div>
      )}

      {/* ── Clinical timeline — relevant dated milestones, colour-coded ──────── */}
      <section>
        <h2 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-ml-on-surface-variant">
          Línea de tiempo clínica
        </h2>
        <div className="rounded-xl border border-ml-outline-variant/15 bg-ml-surface-container-lowest/40 px-5 py-6">
          {milestones.length > 0 ? (
            <div className="overflow-x-auto pb-1">
              <ol className="relative flex min-w-max gap-10">
                {milestones.length > 1 && (
                  <span className="pointer-events-none absolute left-[88px] right-[88px] top-[6px] h-px bg-ml-outline-variant/30" />
                )}
                {milestones.map((m, i) => (
                  <li key={i} className="relative flex w-44 shrink-0 flex-col items-center text-center">
                    <button
                      type="button"
                      onClick={() => {
                        if (m.entry.diagnosis_date) {
                          const match = signedNotes.find(
                            (n) => n.created_at.slice(0, 10) === m.entry.diagnosis_date
                          )
                          if (match) { setOpenNoteId(match.id); return }
                        }
                        setSelectedEntry(m.entry)
                      }}
                      className={cn(
                        'group flex w-full flex-col items-center rounded-lg px-2 py-2 text-center transition-colors',
                        PAL[m.cat].softHover,
                      )}
                      title="Ver detalle"
                    >
                      <span className={cn('relative z-10 h-3 w-3 rounded-full ring-4 ring-ml-surface transition-transform group-hover:scale-125', PAL[m.cat].dot)} />
                      <span className="mt-3 text-sm font-bold text-ml-on-surface">{m.year}</span>
                      <span className="mt-0.5 text-sm font-medium leading-snug text-ml-on-surface">{m.label}</span>
                      {m.entry.item_type && (
                        <span className="mt-1 rounded border border-ml-outline-variant/30 bg-transparent px-2 py-0.5 text-[9px] font-medium text-ml-on-surface-variant/70">
                          {ITEM_TYPE_LABELS[m.entry.item_type] ?? m.entry.item_type}
                        </span>
                      )}
                      <span className={cn('mt-1 text-[10px] font-semibold uppercase tracking-wider', PAL[m.cat].text)}>
                        {m.statusLabel}
                      </span>
                      {m.addedLater && (
                        <span className="mt-1 rounded border border-amber-400/50 bg-amber-50 px-2 py-0.5 text-[9px] font-semibold text-amber-700">
                          Agregado posteriormente
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ol>
            </div>
          ) : (
            <p className="text-sm italic text-ml-on-surface-variant/50">
              Sin eventos clínicos fechados — los antecedentes patológicos con fecha aparecerán aquí como hitos.
            </p>
          )}
        </div>
      </section>

      {/* ── Category accordion — one panel open at a time, soft accents ──────── */}
      <div className="space-y-3">
        {dataPanels.map((p) => (
          <AccordionPanel
            key={p.key}
            title={p.title}
            badge={String(p.count)}
            cat={p.cat}
            isOpen={openKey === p.key}
            onToggle={() => toggle(p.key)}
          >
            {renderBody(p.key)}
          </AccordionPanel>
        ))}

        {/* Observaciones generales */}
        <AccordionPanel
          title="Observaciones generales"
          badge={patient.clinical_observations ? '1' : '0'}
          cat="neutral"
          isOpen={openKey === 'observaciones'}
          onToggle={() => toggle('observaciones')}
        >
          <div className="mb-2 flex justify-end"><EditLink onClick={openObservaciones} /></div>
          {patient.clinical_observations ? (
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-ml-on-surface">{patient.clinical_observations}</p>
          ) : (
            <Placeholder text="Sin observaciones registradas" onAdd={openObservaciones} />
          )}
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-ml-outline-variant/15 pt-3">
            <p className="text-xs text-ml-on-surface-variant/70">
              El historial de notas de evolución se consulta en la pestaña «Evolución» (fuente única, solo lectura).
            </p>
            <button
              type="button"
              onClick={() => navigate(`/workspace/patient/${patient.id}/consulta`)}
              className="text-[11px] font-medium text-ml-on-surface-variant underline-offset-2 hover:text-ml-on-surface hover:underline"
            >
              Ver evolución
            </button>
          </div>
        </AccordionPanel>

        {/* Formal clinical history (NOM-004) — create / fill draft / signed view */}
        <AccordionPanel
          title="Historia clínica formal (NOM-004)"
          badge={clinicalHistory ? (clinicalHistory.status === 'signed' ? 'Firmada' : 'Borrador') : 'Pendiente'}
          cat="info"
          isOpen={openKey === 'nom'}
          onToggle={() => toggle('nom')}
        >
          <ClinicalHistoryDocument patient={patient} />
        </AccordionPanel>
      </div>

      {/* === TIMELINE ENTRY DETAIL DIALOG === */}
      <MilestoneEntryDialog
        entry={selectedEntry}
        signedNotes={signedNotes}
        patientFiles={patientFiles}
        clinicalHistory={clinicalHistory ?? null}
        onClose={() => setSelectedEntry(null)}
        onEdit={() => { setSelectedEntry(null); openCirugias() }}
        onOpenNote={(id) => { setSelectedEntry(null); setOpenNoteId(id) }}
      />

      {/* Note viewer — opened when timeline item matches a signed consultation */}
      <ConsultationNoteDialog consultationId={openNoteId} onClose={() => setOpenNoteId(null)} />

      {/* === FOCUSED EDIT DIALOGS === */}

      {/* Familiares Edit */}
      <Dialog open={editFamiliaresOpen} onOpenChange={setEditFamiliaresOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Antecedentes Familiares</DialogTitle>
            <DialogDescription>Modifique los antecedentes familiares del paciente.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {familiaresForm.map((entry, i) => (
              <div key={i} className="grid grid-cols-3 gap-3 items-end p-3 rounded-lg bg-muted/20 border">
                <div>
                  <label className="text-xs font-medium">Relación</label>
                  <Select value={entry.relation} onValueChange={(v) => { const n = [...familiaresForm]; n[i] = { ...n[i], relation: v }; setFamiliaresForm(n) }}>
                    <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                    <SelectContent>
                      {['padre', 'madre', 'hermano', 'hermana', 'abuelo', 'abuela', 'otro'].map(r => (
                        <SelectItem key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium">Condición</label>
                  <Input value={entry.condition} onChange={(e) => { const n = [...familiaresForm]; n[i] = { ...n[i], condition: e.target.value }; setFamiliaresForm(n) }} placeholder="ej. Hipertensión" />
                </div>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="text-xs font-medium">Notas</label>
                    <Input value={entry.notes ?? ''} onChange={(e) => { const n = [...familiaresForm]; n[i] = { ...n[i], notes: e.target.value }; setFamiliaresForm(n) }} placeholder="Opcional" />
                  </div>
                  <Button variant="ghost" size="icon" className="mt-auto" onClick={() => setFamiliaresForm(familiaresForm.filter((_, j) => j !== i))}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => setFamiliaresForm([...familiaresForm, { relation: '', condition: '', notes: '' }])}>
              <Plus className="h-4 w-4 mr-1" /> Agregar
            </Button>
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setEditFamiliaresOpen(false)}>Cancelar</Button>
              <Button className="bg-ml-primary text-white hover:bg-ml-primary-container" disabled={isPending} onClick={() => {
                saveField('family_history', familiaresForm.filter(e => e.condition.trim()), 'Antecedentes familiares actualizados.')
                setEditFamiliaresOpen(false)
              }}>
                {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Guardar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Social History Edit */}
      <Dialog open={editSocialOpen} onOpenChange={setEditSocialOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Editar Historia Social</DialogTitle>
            <DialogDescription>Modifique los hábitos y el estilo de vida del paciente.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {([
              { key: 'smoking_status' as const, label: 'Tabaquismo', options: [['never','Nunca'],['former','Anteriormente'],['current','Actualmente']] },
              { key: 'alcohol_status' as const, label: 'Alcohol', options: [['never','Nunca'],['former','Anteriormente'],['current','Actualmente']] },
              { key: 'drug_use_status' as const, label: 'Sustancias', options: [['never','Nunca'],['former','Anteriormente'],['current','Actualmente']] },
              { key: 'exercise_status' as const, label: 'Ejercicio', options: [['none','Ninguno'],['occasional','Ocasional'],['regular','Regular']] },
            ]).map(({ key, label, options }) => (
              <div key={key}>
                <label className="text-xs font-medium">{label}</label>
                <Select value={socialForm[key]} onValueChange={(v) => setSocialForm({ ...socialForm, [key]: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {options.map(([val, lbl]) => <SelectItem key={val} value={val}>{lbl}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ))}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setEditSocialOpen(false)}>Cancelar</Button>
              <Button className="bg-ml-primary text-white hover:bg-ml-primary-container" disabled={isPending} onClick={() => {
                saveField('social_history', socialForm, 'Historia social actualizada.')
                setEditSocialOpen(false)
              }}>
                {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Guardar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Alergias Edit */}
      <Dialog open={editAlergiasOpen} onOpenChange={setEditAlergiasOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Alergias</DialogTitle>
            <DialogDescription>Modifique las alergias del paciente.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {alergiasForm.map((allergy, i) => (
              <div key={i} className="p-3 rounded-lg bg-muted/20 border space-y-3">
                <div className="flex gap-3">
                  <div className="flex-1">
                    <label className="text-xs font-medium">Nombre</label>
                    <Input value={allergy.name} onChange={(e) => { const n = [...alergiasForm]; n[i] = { ...n[i], name: e.target.value }; setAlergiasForm(n) }} placeholder="Nombre de la alergia" />
                  </div>
                  <Button variant="ghost" size="icon" className="mt-auto" onClick={() => setAlergiasForm(alergiasForm.filter((_, j) => j !== i))}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs font-medium">Tipo</label>
                    <Select value={allergy.type} onValueChange={(v) => { const n = [...alergiasForm]; n[i] = { ...n[i], type: v as AllergyEntry['type'] }; setAlergiasForm(n) }}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="drug">Medicamento</SelectItem>
                        <SelectItem value="food">Alimento</SelectItem>
                        <SelectItem value="environmental">Ambiental</SelectItem>
                        <SelectItem value="other">Otro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-medium">Severidad</label>
                    <Select value={allergy.severity} onValueChange={(v) => { const n = [...alergiasForm]; n[i] = { ...n[i], severity: v as AllergyEntry['severity'] }; setAlergiasForm(n) }}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mild">Leve</SelectItem>
                        <SelectItem value="moderate">Moderada</SelectItem>
                        <SelectItem value="severe">Severa</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-medium">Reacción</label>
                    <Input value={allergy.reaction} onChange={(e) => { const n = [...alergiasForm]; n[i] = { ...n[i], reaction: e.target.value }; setAlergiasForm(n) }} placeholder="Descripción" />
                  </div>
                </div>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => setAlergiasForm([...alergiasForm, { name: '', type: 'other', severity: 'moderate', reaction: '' }])}>
              <Plus className="h-4 w-4 mr-1" /> Agregar Alergia
            </Button>
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setEditAlergiasOpen(false)}>Cancelar</Button>
              <Button className="bg-ml-primary text-white hover:bg-ml-primary-container" disabled={isPending} onClick={() => {
                saveField('allergies', alergiasForm.filter(a => a.name.trim()), 'Alergias actualizadas.')
                setEditAlergiasOpen(false)
              }}>
                {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Guardar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Tratamientos Edit */}
      <Dialog open={editTratamientosOpen} onOpenChange={setEditTratamientosOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Tratamientos Actuales</DialogTitle>
            <DialogDescription>
              Registre los medicamentos o tratamientos vigentes del paciente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {tratamientosForm.map((tratamiento, index) => (
              <div
                key={index}
                className="flex items-end gap-3 rounded-lg border bg-muted/20 p-3"
              >
                <div className="flex-1">
                  <label className="text-xs font-medium">Tratamiento</label>
                  <Input
                    value={tratamiento}
                    onChange={(e) => {
                      const next = [...tratamientosForm]
                      next[index] = e.target.value
                      setTratamientosForm(next)
                    }}
                    placeholder="ej. Metformina 850 mg cada 12 horas"
                  />
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() =>
                    setTratamientosForm(tratamientosForm.filter((_, itemIndex) => itemIndex !== index))
                  }
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            ))}

            <Button
              variant="outline"
              size="sm"
              onClick={() => setTratamientosForm([...tratamientosForm, ''])}
            >
              <Plus className="h-4 w-4 mr-1" />
              Agregar Tratamiento
            </Button>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setEditTratamientosOpen(false)}>
                Cancelar
              </Button>
              <Button
                className="bg-ml-primary text-white hover:bg-ml-primary-container"
                disabled={isPending}
                onClick={() => {
                  saveField(
                    'current_medications',
                    tratamientosForm.map((item) => item.trim()).filter(Boolean),
                    'Tratamientos actualizados.'
                  )
                  setEditTratamientosOpen(false)
                }}
              >
                {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Guardar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cirugias Edit */}
      <Dialog open={editCirugiasOpen} onOpenChange={setEditCirugiasOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Cirugías y Hospitalizaciones</DialogTitle>
            <DialogDescription>Modifique los antecedentes patológicos del paciente.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {cirugiasForm.map((entry, i) => (
              <div key={i} className="p-3 rounded-lg bg-muted/20 border space-y-3">
                <div className="flex gap-3">
                  <div className="flex-1">
                    <label className="text-xs font-medium">Condición</label>
                    <Input value={entry.condition} onChange={(e) => { const n = [...cirugiasForm]; n[i] = { ...n[i], condition: e.target.value }; setCirugiasForm(n) }} placeholder="ej. Apendicectomía" />
                  </div>
                  <Button variant="ghost" size="icon" className="mt-auto" onClick={() => setCirugiasForm(cirugiasForm.filter((_, j) => j !== i))}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs font-medium">Estado</label>
                    <Select value={entry.status} onValueChange={(v) => { const n = [...cirugiasForm]; n[i] = { ...n[i], status: v as PathologicalEntry['status'] }; setCirugiasForm(n) }}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Activo</SelectItem>
                        <SelectItem value="resolved">Resuelto</SelectItem>
                        <SelectItem value="chronic">Crónico</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-medium">Fecha</label>
                    <Input type="date" value={entry.diagnosis_date ?? ''} onChange={(e) => { const n = [...cirugiasForm]; n[i] = { ...n[i], diagnosis_date: e.target.value }; setCirugiasForm(n) }} />
                  </div>
                  <div>
                    <label className="text-xs font-medium">Notas</label>
                    <Input value={entry.notes ?? ''} onChange={(e) => { const n = [...cirugiasForm]; n[i] = { ...n[i], notes: e.target.value }; setCirugiasForm(n) }} placeholder="Opcional" />
                  </div>
                </div>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => setCirugiasForm([...cirugiasForm, { condition: '', status: 'active', diagnosis_date: '', notes: '' }])}>
              <Plus className="h-4 w-4 mr-1" /> Agregar
            </Button>
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setEditCirugiasOpen(false)}>Cancelar</Button>
              <Button className="bg-ml-primary text-white hover:bg-ml-primary-container" disabled={isPending} onClick={() => {
                saveField('pathological_history', cirugiasForm.filter(e => e.condition.trim()), 'Antecedentes patológicos actualizados.')
                setEditCirugiasOpen(false)
              }}>
                {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Guardar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Observaciones Edit */}
      <Dialog open={editObservacionesOpen} onOpenChange={setEditObservacionesOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Editar Observaciones Generales</DialogTitle>
            <DialogDescription>Agregue o modifique las observaciones clínicas generales.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Textarea
              value={observacionesForm}
              onChange={(e) => setObservacionesForm(e.target.value)}
              placeholder="Escriba las observaciones clínicas generales del paciente..."
              rows={8}
              className="resize-none"
            />
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setEditObservacionesOpen(false)}>Cancelar</Button>
              <Button className="bg-ml-primary text-white hover:bg-ml-primary-container" disabled={isPending} onClick={() => {
                saveField('clinical_observations', observacionesForm.trim() || undefined, 'Observaciones actualizadas.')
                setEditObservacionesOpen(false)
              }}>
                {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Guardar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Gineco-Obstetricos Edit (external dialog) */}
      {isFemale && (
        <EditGinecoObstetricosDialog
          patient={patient}
          open={ginecoEditOpen}
          onOpenChange={setGinecoEditOpen}
        />
      )}

      {/* Add Individual History Item */}
      <AddHistoryItemDialog
        open={addHistoryItemOpen}
        onOpenChange={setAddHistoryItemOpen}
        existingItems={patologicos}
        capturedByName={currentUser ? `${currentUser.first_name} ${currentUser.last_name}`.trim() : undefined}
        isSaving={isPending}
        onSave={handleSaveHistoryItem}
      />
    </div>
  )
}

// ── Presentational helpers ───────────────────────────────────────────────────

/** Collapsible category panel: a header with a soft category accent (dot + left
 *  bar + outlined count badge) and a chevron, with content shown when open. */
function AccordionPanel({
  title,
  badge,
  cat,
  isOpen,
  onToggle,
  children,
}: {
  title: string
  badge: React.ReactNode
  cat: Cat
  isOpen: boolean
  onToggle: () => void
  children: React.ReactNode
}) {
  const pal = PAL[cat]
  return (
    <div className="overflow-hidden rounded-xl border border-ml-outline-variant/20 bg-ml-surface">
      <button
        type="button"
        onClick={onToggle}
        className={cn('flex w-full items-center justify-between gap-3 border-l-2 px-4 py-3 text-left transition-colors', pal.bar, pal.soft, pal.softHover)}
      >
        <div className="flex min-w-0 items-center gap-2.5">
          <span className={cn('h-2 w-2 shrink-0 rounded-full', pal.dot)} />
          <h3 className="truncate text-[12px] font-semibold uppercase tracking-[0.1em] text-ml-on-surface">{title}</h3>
          <span className={cn('shrink-0 rounded border bg-transparent px-2 py-0.5 text-[10px] font-semibold', pal.chip)}>
            {badge}
          </span>
        </div>
        <ChevronDown className={cn('h-4 w-4 shrink-0 text-ml-on-surface-variant transition-transform', isOpen && 'rotate-180')} />
      </button>
      {isOpen && <div className="border-t border-ml-outline-variant/15 px-4 py-4">{children}</div>}
    </div>
  )
}

/** A compact data table for a history collection. */
function HistoryTable({ head, rows }: { head: string[]; rows: string[][] }) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-[10px] uppercase tracking-wider text-ml-on-surface-variant/60">
          {head.map((h, i) => (
            <th key={i} className={cn('pb-1.5 font-medium', i < head.length - 1 && 'pr-4')}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} className="border-t border-ml-outline-variant/15 align-top transition-colors hover:bg-ml-surface-container-low/40">
            {row.map((cell, j) => (
              <td key={j} className={cn('py-2', j < row.length - 1 && 'pr-4', j === 0 ? 'font-medium text-ml-on-surface' : 'text-ml-on-surface-variant')}>
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  )
}

/** A lighter heading for sub-groups inside a panel (e.g. within "patológicos"). */
function SubBlock({
  title,
  action,
  children,
}: {
  title: string
  action?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-3">
        <h4 className="text-[10px] font-semibold uppercase tracking-[0.15em] text-ml-on-surface-variant/80">
          {title}
        </h4>
        {action}
      </div>
      {children}
    </div>
  )
}

/** A small label-above-value cell; empty values read as muted-italic "Pendiente". */
function Stat({ label, value }: { label: string; value?: string | null }) {
  const empty = value == null || value === ''
  return (
    <div>
      <p className="text-[11px] font-normal text-ml-on-surface-variant">{label}</p>
      <p className={cn('text-sm', empty ? 'italic text-ml-on-surface-variant/50' : 'font-medium text-ml-on-surface')}>
        {empty ? 'Pendiente' : value}
      </p>
    </div>
  )
}

/** A discreet placeholder for an empty section, with a clear inline "add" action. */
function Placeholder({ text, onAdd }: { text: string; onAdd?: () => void }) {
  return (
    <p className="text-sm italic text-ml-on-surface-variant/50">
      {text}
      {onAdd && (
        <>
          {' — '}
          <button
            type="button"
            onClick={onAdd}
            className="font-medium not-italic text-ml-on-surface-variant underline-offset-2 hover:text-ml-on-surface hover:underline"
          >
            agregar
          </button>
        </>
      )}
    </p>
  )
}

/** Inline "Editar" text link used in panel/sub-block headers. */
function EditLink({ onClick, children = 'Editar' }: { onClick: () => void; children?: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="shrink-0 text-[11px] font-medium text-ml-on-surface-variant underline-offset-2 hover:text-ml-on-surface hover:underline"
    >
      {children}
    </button>
  )
}

// ── MilestoneEntryDialog ─────────────────────────────────────────────────────

const FILE_TYPE_ICON: Record<string, string> = {
  pdf: '📄',
  jpg: '🖼',
  png: '🖼',
  tiff: '🖼',
  doc: '📝',
  docx: '📝',
}

function fmtDate(iso: string) {
  return new Date(iso + (iso.includes('T') ? '' : 'T12:00:00')).toLocaleDateString('es-MX', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

function relativeYears(isoDate: string) {
  const diff = new Date().getFullYear() - new Date(isoDate + 'T12:00:00').getFullYear()
  if (diff === 0) return 'este año'
  return `hace ${diff} ${diff === 1 ? 'año' : 'años'}`
}

/** Section heading used inside the detail dialog. */
function DSection({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="mb-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-ml-on-surface-variant/60">
        {label}
      </p>
      {children}
    </div>
  )
}

/** Context-aware milestone detail dialog.
 *  - Resolved → surgical/procedural layout
 *  - Active/Chronic → ongoing-condition layout
 *  - Related files matched by ±60-day proximity to diagnosis date
 *  - Closest note surfaced when no exact-date match
 */
function MilestoneEntryDialog({
  entry,
  signedNotes,
  patientFiles,
  clinicalHistory,
  onClose,
  onEdit,
  onOpenNote,
}: {
  entry: PathologicalEntry | null
  signedNotes: ConsultationSummary[]
  patientFiles: LabFile[]
  clinicalHistory: ClinicalHistory | null
  onClose: () => void
  onEdit: () => void
  onOpenNote: (id: string) => void
}) {
  if (!entry) return null

  const cat = entry.status === 'resolved' ? 'surgical' : entry.status === 'active' || entry.status === 'chronic' ? 'active' : 'neutral'
  const pal = PAL[cat as Cat]

  const diagDate = entry.diagnosis_date ? new Date(entry.diagnosis_date + 'T12:00:00') : null

  // Files uploaded within 60 days of the diagnosis date (best-effort proximity)
  const relatedFiles = diagDate
    ? patientFiles
        .filter((f) => {
          const diff = Math.abs(new Date(f.uploaded_at).getTime() - diagDate.getTime()) / 86400000
          return diff <= 60
        })
        .sort((a, b) => new Date(a.uploaded_at).getTime() - new Date(b.uploaded_at).getTime())
        .slice(0, 4)
    : []

  // Closest signed note within 30 days (only shown when no exact-date match opens ConsultationNoteDialog)
  const closestNote = diagDate
    ? signedNotes
        .map((n) => ({ n, diff: Math.abs(new Date(n.created_at).getTime() - diagDate.getTime()) / 86400000 }))
        .filter((x) => x.diff > 0 && x.diff <= 30)
        .sort((a, b) => a.diff - b.diff)[0]?.n ?? null
    : null

  const hasAddenda = (clinicalHistory?.addenda?.length ?? 0) > 0

  const CategoryIcon = cat === 'surgical' ? Scissors : cat === 'active' ? (entry.status === 'chronic' ? RefreshCw : Activity) : Activity

  return (
    <Dialog open={!!entry} onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <div className={cn('mb-1 flex h-8 w-8 items-center justify-center rounded-lg', pal.soft)}>
            <CategoryIcon className={cn('h-4 w-4', pal.text)} />
          </div>
          <DialogTitle className="text-base font-semibold leading-snug text-ml-on-surface">
            {entry.condition}
          </DialogTitle>
          <DialogDescription asChild>
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <span className={cn('rounded border bg-transparent px-2.5 py-0.5 text-xs font-semibold', pal.chip)}>
                {statusLabels[entry.status] ?? entry.status}
              </span>
              {entry.diagnosis_date && (
                <span className="flex items-center gap-1 text-xs text-ml-on-surface-variant">
                  <CalendarDays className="h-3 w-3" />
                  {fmtDate(entry.diagnosis_date)}
                </span>
              )}
              {hasAddenda && (
                <span className="rounded border border-amber-400/50 bg-transparent px-2 py-0.5 text-[10px] font-semibold text-amber-700">
                  Historia con correcciones
                </span>
              )}
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 space-y-5">
          {/* ── Surgical / Resolved ─────────────────────────────────────── */}
          {cat === 'surgical' && (
            <>
              <DSection label="Estado actual">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  <span className="text-sm font-medium text-emerald-700">
                    Resuelto — sin seguimiento activo requerido
                  </span>
                </div>
              </DSection>

              {entry.diagnosis_date && (
                <DSection label="Fecha del procedimiento / diagnóstico">
                  <p className="text-sm text-ml-on-surface">
                    {fmtDate(entry.diagnosis_date)}
                    <span className="ml-2 text-xs text-ml-on-surface-variant/60">
                      ({relativeYears(entry.diagnosis_date)})
                    </span>
                  </p>
                </DSection>
              )}

              <DSection label="Detalles del procedimiento / notas del médico">
                {entry.notes ? (
                  <p className="whitespace-pre-wrap rounded-lg border border-ml-outline-variant/20 bg-ml-surface-container-lowest/60 px-3 py-2.5 text-sm leading-relaxed text-ml-on-surface">
                    {entry.notes}
                  </p>
                ) : (
                  <p className="text-sm italic text-ml-on-surface-variant/50">Sin notas registradas.</p>
                )}
              </DSection>
            </>
          )}

          {/* ── Active / Chronic ─────────────────────────────────────────── */}
          {cat === 'active' && (
            <>
              <DSection label="Estado actual">
                <div className="flex items-center gap-2">
                  <CategoryIcon className={cn('h-4 w-4', pal.text)} />
                  <span className={cn('text-sm font-medium', pal.text)}>
                    {entry.status === 'chronic'
                      ? 'Crónico — seguimiento continuo requerido'
                      : 'Activo — en evaluación y seguimiento'}
                  </span>
                </div>
              </DSection>

              {entry.diagnosis_date ? (
                <DSection label="Diagnóstico inicial">
                  <p className="text-sm text-ml-on-surface">
                    {fmtDate(entry.diagnosis_date)}
                    <span className="ml-2 text-xs text-ml-on-surface-variant/60">
                      ({relativeYears(entry.diagnosis_date)})
                    </span>
                  </p>
                </DSection>
              ) : (
                <DSection label="Diagnóstico inicial">
                  <p className="text-sm italic text-ml-on-surface-variant/50">Fecha no especificada.</p>
                </DSection>
              )}

              <DSection label={entry.status === 'chronic' ? 'Manejo crónico / notas del médico' : 'Notas del médico'}>
                {entry.notes ? (
                  <p className="whitespace-pre-wrap rounded-lg border border-ml-outline-variant/20 bg-ml-surface-container-lowest/60 px-3 py-2.5 text-sm leading-relaxed text-ml-on-surface">
                    {entry.notes}
                  </p>
                ) : (
                  <p className="text-sm italic text-ml-on-surface-variant/50">Sin notas registradas.</p>
                )}
              </DSection>

              <div className="rounded-lg border border-ml-outline-variant/20 bg-ml-surface-container-lowest/40 px-3 py-3">
                <p className="text-[11px] text-ml-on-surface-variant/70">
                  Para registrar próxima acción o seguimiento, agréguela en la nota de evolución de la próxima consulta.
                </p>
              </div>
            </>
          )}

          {/* ── Rich type-specific details ───────────────────────────────── */}
          <TypeSpecificDetails entry={entry} />

          {/* ── Traceability: captured by / added later ───────────────────── */}
          {entry.captured_by_name && (
            <div className="rounded-lg border border-ml-outline-variant/20 bg-ml-surface-container-lowest/40 px-3 py-2.5">
              <p className="text-[11px] text-ml-on-surface-variant/70">
                Registrado por{' '}
                <span className="font-medium text-ml-on-surface">{entry.captured_by_name}</span>
                {entry.captured_at && (
                  <>
                    {' el '}
                    {new Date(entry.captured_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </>
                )}
              </p>
              {clinicalHistory?.status === 'signed' && clinicalHistory.attested_at && entry.captured_at && entry.captured_at > clinicalHistory.attested_at && (
                <p className="mt-1 rounded-md border border-amber-300/50 bg-amber-50 px-2 py-1 text-[11px] font-semibold text-amber-700">
                  ⚠ Agregado posteriormente — no formaba parte de la historia clínica original firmada.
                </p>
              )}
            </div>
          )}

          {/* ── Related item ─────────────────────────────────────────────── */}
          {entry.related_item_idx != null && (
            <DSection label="Antecedente relacionado">
              <p className="text-sm text-ml-on-surface">
                {/* Index-based reference shown inline */}
                Ítem #{entry.related_item_idx + 1}
              </p>
            </DSection>
          )}

          {/* ── Related files (heuristic ±60 days) ──────────────────────── */}
          {relatedFiles.length > 0 && (
            <DSection label="Archivos del período (±60 días)">
              <ul className="space-y-1.5">
                {relatedFiles.map((f) => (
                  <li key={f.id}>
                    <button
                      type="button"
                      onClick={() => window.open(f.download_url, '_blank')}
                      className="flex w-full items-center gap-2.5 rounded-md border border-ml-outline-variant/20 bg-ml-surface px-3 py-2 text-left text-xs transition-colors hover:bg-ml-surface-container-low"
                    >
                      <span className="text-base leading-none">{FILE_TYPE_ICON[f.file_type] ?? '📎'}</span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium text-ml-on-surface">{f.original_name}</span>
                        <span className="text-ml-on-surface-variant/60">
                          {new Date(f.uploaded_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })}
                          {f.file_size_human && ` · ${f.file_size_human}`}
                        </span>
                      </span>
                      <ExternalLink className="h-3.5 w-3.5 shrink-0 text-ml-on-surface-variant/40" />
                    </button>
                  </li>
                ))}
              </ul>
            </DSection>
          )}

          {relatedFiles.length === 0 && (
            <DSection label="Archivos relacionados">
              <p className="text-sm italic text-ml-on-surface-variant/50">Sin archivos vinculados al período.</p>
            </DSection>
          )}

          {/* ── Source traceability ──────────────────────────────────────── */}
          {clinicalHistory && (
            <div className={cn('flex items-start gap-2 rounded-lg border px-3 py-2.5 text-xs',
              clinicalHistory.status === 'signed'
                ? 'border-emerald-200/60 bg-emerald-50/40 text-emerald-800'
                : 'border-ml-outline-variant/20 bg-ml-surface-container-lowest/40 text-ml-on-surface-variant'
            )}>
              {clinicalHistory.status === 'signed' ? (
                <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
              ) : (
                <FileText className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ml-on-surface-variant/60" />
              )}
              <span>
                {clinicalHistory.status === 'signed'
                  ? <>Historia clínica formal firmada{clinicalHistory.attested_by_name ? <> por <strong>{clinicalHistory.attested_by_name}</strong></> : ''}{clinicalHistory.attested_at ? <> · {fmtDate(clinicalHistory.attested_at)}</> : ''}.</>
                  : 'Historia clínica formal en borrador — pendiente de firma.'}
                {hasAddenda && (
                  <span className="ml-1 font-medium text-amber-700">
                    ({clinicalHistory.addenda!.length} corrección{clinicalHistory.addenda!.length !== 1 ? 'es' : ''} registrada{clinicalHistory.addenda!.length !== 1 ? 's' : ''} en la historia.)
                  </span>
                )}
              </span>
            </div>
          )}
        </div>

        {/* ── Footer ───────────────────────────────────────────────────── */}
        <div className="mt-4 space-y-2 border-t border-ml-outline-variant/15 pt-3">
          {closestNote && (
            <div className="flex items-center justify-between gap-3 rounded-md bg-ml-surface-container-lowest/60 px-3 py-2">
              <p className="text-[11px] text-ml-on-surface-variant/70">
                Nota de evolución próxima al diagnóstico:
                <span className="ml-1 font-medium">
                  {new Date(closestNote.created_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })}
                </span>
              </p>
              <button
                type="button"
                onClick={() => onOpenNote(closestNote.id)}
                className="flex shrink-0 items-center gap-1 text-[11px] font-medium text-ml-on-surface-variant underline-offset-2 hover:text-ml-on-surface hover:underline"
              >
                Ver nota <ExternalLink className="h-3 w-3" />
              </button>
            </div>
          )}
          {!closestNote && (
            <p className="text-[11px] text-ml-on-surface-variant/50">
              {entry.diagnosis_date
                ? 'Sin nota de evolución registrada para esta fecha ni en los 30 días anteriores.'
                : 'Sin fecha de diagnóstico — no se puede buscar nota asociada.'}
            </p>
          )}
          <div className="flex justify-end">
            <Button variant="outline" size="sm" onClick={onEdit}>
              Editar antecedente
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ── TypeSpecificDetails ──────────────────────────────────────────────────────

function InfoRow({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null
  return (
    <div className="flex gap-2 text-sm">
      <span className="min-w-[120px] shrink-0 text-[11px] font-semibold uppercase tracking-wide text-ml-on-surface-variant/60">{label}</span>
      <span className="text-ml-on-surface">{value}</span>
    </div>
  )
}

const SEVERITY_ES: Record<string, string> = { mild: 'Leve', moderate: 'Moderada', severe: 'Severa' }

function TypeSpecificDetails({ entry }: { entry: PathologicalEntry }) {
  const rows: { label: string; value?: string | null }[] = []

  if (entry.item_type === 'surgery') {
    rows.push(
      { label: 'Procedimiento', value: entry.surgery_procedure },
      { label: 'Hospital', value: entry.surgery_hospital },
      { label: 'Cirujano', value: entry.surgery_physician },
    )
  } else if (entry.item_type === 'allergy') {
    rows.push(
      { label: 'Agente causante', value: entry.allergy_causative_agent },
      { label: 'Tipo de reacción', value: entry.allergy_reaction_type },
      { label: 'Severidad', value: entry.allergy_severity ? SEVERITY_ES[entry.allergy_severity] : undefined },
    )
  } else if (entry.item_type === 'chronic_degenerative') {
    rows.push(
      { label: 'Tratamiento actual', value: entry.chronic_treatment },
      { label: 'Especialista', value: entry.chronic_specialist },
    )
  } else if (entry.item_type === 'transfusion') {
    rows.push(
      { label: 'Motivo', value: entry.transfusion_reason },
      { label: 'Hemoderivado', value: entry.transfusion_blood_product },
      { label: 'Reacciones', value: entry.transfusion_reaction },
    )
  } else if (entry.item_type === 'hospitalization') {
    rows.push(
      { label: 'Motivo', value: entry.hospitalization_reason },
      { label: 'Duración', value: entry.hospitalization_duration },
      { label: 'Hospital', value: entry.hospitalization_hospital },
    )
  } else if (entry.item_type === 'vaccination') {
    rows.push(
      { label: 'Vacuna', value: entry.vaccination_name },
      { label: 'Dosis', value: entry.vaccination_dose },
    )
  } else if (entry.item_type === 'current_medication') {
    rows.push(
      { label: 'Medicamento', value: entry.medication_name },
      { label: 'Dosis', value: entry.medication_dosage },
      { label: 'Frecuencia', value: entry.medication_frequency },
      { label: 'Desde', value: entry.medication_start_date },
    )
  }

  const hasPhysicianNotes = !!(entry.physician_notes || entry.notes)
  const hasRows = rows.some((r) => r.value)

  if (!hasRows && !hasPhysicianNotes) return null

  return (
    <DSection label={entry.item_type ? (ITEM_TYPE_LABELS[entry.item_type] ?? 'Detalles') : 'Detalles'}>
      <div className="rounded-lg border border-ml-outline-variant/20 bg-ml-surface-container-lowest/60 px-3 py-2.5 space-y-2">
        {rows.map((r) => <InfoRow key={r.label} label={r.label} value={r.value} />)}
        {hasPhysicianNotes && (
          <div className="pt-1 border-t border-ml-outline-variant/15">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-ml-on-surface-variant/60 mb-1">Notas del médico</p>
            <p className="text-sm text-ml-on-surface whitespace-pre-wrap">{entry.physician_notes || entry.notes}</p>
          </div>
        )}
      </div>
    </DSection>
  )
}
